import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Form, InputNumber, Input, Modal, Space, Switch, Table, Tag, Tooltip, Typography, Upload, message,
} from 'antd';
import { DownloadOutlined, PlusOutlined, UploadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import {
  activateRatingTemplate, checkRatingTemplate, createRatingTemplate, downloadRatingTemplate,
  getRatingTemplates, newRatingTemplateVersion, uploadRatingTemplate,
} from '../../services/api';
import { getApiError } from '../../utils/apiError';

const { Text } = Typography;

interface RatingTemplate {
  id: string;
  client_name: string;
  name: string;
  version: number;
  status: 'DRAFT' | 'ACTIVE' | 'RETIRED';
  is_locked: boolean;
  item_count: number;
  created_at: string;
}

interface UploadResult {
  errors: string[];
  problems: string[];
  saved: boolean;
}

const STATUS_COLOUR: { [status: string]: string } = { DRAFT: 'orange', ACTIVE: 'green', RETIRED: 'default' };

const TemplatesTab: React.FC = () => {
  const [templates, setTemplates] = useState<RatingTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();
  const blendEnabled = Form.useWatch('past_blend_enabled', form);

  const [uploadFor, setUploadFor] = useState<RatingTemplate | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [uploading, setUploading] = useState(false);

  const [checkFor, setCheckFor] = useState<RatingTemplate | null>(null);
  const [problems, setProblems] = useState<string[] | null>(null);
  const [activating, setActivating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getRatingTemplates();
      setTemplates(res.data);
    } catch (err) {
      setError(getApiError(err, 'Failed to load rating templates.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setCreating(true);
      await createRatingTemplate({
        client_name: values.client_name,
        name: values.name,
        band_green_min: values.band_green_min,
        band_yellow_min: values.band_yellow_min,
        past_blend_enabled: !!values.past_blend_enabled,
        current_weight_pct: values.current_weight_pct,
        nc_level_weights: {
          CRITICAL: values.w_critical, MAJOR: values.w_major, MINOR: values.w_minor, CONCERN: values.w_concern,
        },
      });
      message.success('Template created with the full question library. Download it to fill in the weights.');
      setCreateOpen(false);
      load();
    } catch (err: any) {
      if (!err?.errorFields) message.error(getApiError(err, 'Failed to create the template.'), 8);
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async (template: RatingTemplate) => {
    try {
      const res = await downloadRatingTemplate(template.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `rating_template_${template.client_name}_v${template.version}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error(getApiError(err, 'Failed to download the template.'), 8);
    }
  };

  const handleNewVersion = (template: RatingTemplate) => {
    Modal.confirm({
      title: `Create version ${template.version + 1}?`,
      content: 'A copy of this template becomes a new draft version. Edit it by downloading and uploading the Excel, then activate it and attach it to the project. Visits already started keep their version.',
      okText: 'Create new version',
      onOk: async () => {
        try {
          await newRatingTemplateVersion(template.id);
          message.success('New draft version created.');
          load();
        } catch (err) {
          message.error(getApiError(err, 'Could not create the new version.'), 8);
        }
      },
    });
  };

  const openUpload = (template: RatingTemplate) => {
    setUploadFor(template);
    setFile(null);
    setUploadResult(null);
  };

  const runUpload = async (save: boolean) => {
    if (!uploadFor || !file) return;
    setUploading(true);
    try {
      const res = await uploadRatingTemplate(uploadFor.id, file, save);
      setUploadResult(res.data);
      if (save) {
        message.success('Template saved.');
        load();
      }
    } catch (err: any) {
      if (err?.response?.data?.errors) setUploadResult(err.response.data);
      else message.error(getApiError(err, 'Failed to read the file.'), 8);
    } finally {
      setUploading(false);
    }
  };

  const openCheck = async (template: RatingTemplate) => {
    setCheckFor(template);
    setProblems(null);
    try {
      const res = await checkRatingTemplate(template.id);
      setProblems(res.data.problems);
    } catch (err) {
      message.error(getApiError(err, 'Failed to check the template.'), 8);
      setCheckFor(null);
    }
  };

  const handleActivate = async () => {
    if (!checkFor) return;
    setActivating(true);
    try {
      await activateRatingTemplate(checkFor.id);
      message.success('Template activated. It can now be assigned to projects.');
      setCheckFor(null);
      load();
    } catch (err) {
      message.error(getApiError(err, 'Failed to activate the template.'), 8);
    } finally {
      setActivating(false);
    }
  };

  const columns = [
    { title: 'Client', dataIndex: 'client_name', key: 'client_name' },
    { title: 'Template', dataIndex: 'name', key: 'name' },
    { title: 'Version', dataIndex: 'version', key: 'version', width: 80 },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 110,
      render: (status: string) => <Tag color={STATUS_COLOUR[status]}>{status}</Tag>,
    },
    { title: 'Points', dataIndex: 'item_count', key: 'item_count', width: 90 },
    {
      title: 'Locked', dataIndex: 'is_locked', key: 'is_locked', width: 90,
      render: (locked: boolean) => (locked ? <Tag>Locked</Tag> : '-'),
    },
    {
      title: 'Created', dataIndex: 'created_at', key: 'created_at', width: 120,
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
    {
      title: 'Actions', key: 'actions', width: 340,
      render: (_: unknown, template: RatingTemplate) => (
        <Space>
          <Tooltip title="Download as Excel">
            <Button icon={<DownloadOutlined />} size="small" onClick={() => handleDownload(template)} />
          </Tooltip>
          {(template.is_locked || template.status === 'ACTIVE') && (
            <Button size="small" onClick={() => handleNewVersion(template)}>New version</Button>
          )}
          {template.status === 'DRAFT' && !template.is_locked && (
            <>
              <Button icon={<UploadOutlined />} size="small" onClick={() => openUpload(template)}>Upload</Button>
              <Button icon={<CheckCircleOutlined />} size="small" onClick={() => openCheck(template)}>
                Check / Activate
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Text type="secondary">
          A template is a client's version of the checklist: what applies, how it is weighted and banded.
        </Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setCreateOpen(true); }}>
          New Template
        </Button>
      </div>

      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

      <Table
        rowKey="id" columns={columns} dataSource={templates} loading={loading}
        pagination={{ pageSize: 10 }} size="middle"
      />

      <Modal
        title="New rating template" open={createOpen} onOk={handleCreate} confirmLoading={creating}
        onCancel={() => setCreateOpen(false)} okText="Create" width={560}
      >
        <Form
          form={form} layout="vertical"
          initialValues={{
            band_green_min: 9, band_yellow_min: 7.5, past_blend_enabled: false, current_weight_pct: 75,
            w_critical: 4, w_major: 3, w_minor: 2, w_concern: 1,
          }}
        >
          <Form.Item name="client_name" label="Client" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="name" label="Template name" rules={[{ required: true }]}><Input /></Form.Item>
          <Space size="large">
            <Form.Item name="band_green_min" label="Green from (score)"><InputNumber min={0} max={10} step={0.1} /></Form.Item>
            <Form.Item name="band_yellow_min" label="Yellow from (score)"><InputNumber min={0} max={10} step={0.1} /></Form.Item>
          </Space>
          <Form.Item name="past_blend_enabled" label="Blend in the previous audit's NC closure" valuePropName="checked">
            <Switch />
          </Form.Item>
          {blendEnabled && (
            <>
              <Form.Item name="current_weight_pct" label="Weight of the current audit (%)">
                <InputNumber min={0} max={100} />
              </Form.Item>
              <Space size="large">
                <Form.Item name="w_critical" label="Critical NC weight"><InputNumber min={0} /></Form.Item>
                <Form.Item name="w_major" label="Major"><InputNumber min={0} /></Form.Item>
                <Form.Item name="w_minor" label="Minor"><InputNumber min={0} /></Form.Item>
                <Form.Item name="w_concern" label="Concern"><InputNumber min={0} /></Form.Item>
              </Space>
            </>
          )}
        </Form>
      </Modal>

      <Modal
        title={uploadFor ? `Upload filled template - ${uploadFor.client_name} v${uploadFor.version}` : ''}
        open={!!uploadFor} onCancel={() => setUploadFor(null)} width={640}
        footer={[
          <Button key="close" onClick={() => setUploadFor(null)}>Close</Button>,
          <Button key="check" disabled={!file} loading={uploading} onClick={() => runUpload(false)}>
            Check file
          </Button>,
          <Button
            key="save" type="primary" loading={uploading}
            disabled={!file || !uploadResult || uploadResult.errors.length > 0 || uploadResult.saved}
            onClick={() => runUpload(true)}
          >
            Save to template
          </Button>,
        ]}
      >
        <Upload
          accept=".xlsx" maxCount={1} beforeUpload={(f) => { setFile(f); setUploadResult(null); return false; }}
          onRemove={() => { setFile(null); setUploadResult(null); }}
        >
          <Button icon={<UploadOutlined />}>Choose the Excel file</Button>
        </Upload>
        <div style={{ marginTop: 16 }}>
          {uploadResult && uploadResult.errors.length > 0 && (
            <Alert
              type="error" showIcon message="The file cannot be saved. Fix these and check again:"
              description={<ul style={{ margin: 0, paddingLeft: 18 }}>{uploadResult.errors.map((e) => <li key={e}>{e}</li>)}</ul>}
            />
          )}
          {uploadResult && uploadResult.errors.length === 0 && (
            <Alert
              type={uploadResult.problems.length ? 'warning' : 'success'} showIcon
              message={uploadResult.saved ? 'Saved.' : 'The file is fine and can be saved.'}
              description={uploadResult.problems.length ? (
                <>
                  <div>Still to complete before the template can be activated:</div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>{uploadResult.problems.map((p) => <li key={p}>{p}</li>)}</ul>
                </>
              ) : 'Nothing is missing; the template can be activated.'}
            />
          )}
        </div>
      </Modal>

      <Modal
        title={checkFor ? `Check - ${checkFor.client_name} v${checkFor.version}` : ''}
        open={!!checkFor} onCancel={() => setCheckFor(null)} width={640}
        footer={[
          <Button key="close" onClick={() => setCheckFor(null)}>Close</Button>,
          <Button
            key="activate" type="primary" loading={activating}
            disabled={problems === null || problems.length > 0} onClick={handleActivate}
          >
            Activate
          </Button>,
        ]}
      >
        {problems === null && <Text type="secondary">Checking...</Text>}
        {problems !== null && problems.length === 0 && (
          <Alert type="success" showIcon message="Everything is in order. The template can be activated." />
        )}
        {problems !== null && problems.length > 0 && (
          <Alert
            type="warning" showIcon message="Not ready to activate yet:"
            description={<ul style={{ margin: 0, paddingLeft: 18 }}>{problems.map((p) => <li key={p}>{p}</li>)}</ul>}
          />
        )}
      </Modal>
    </>
  );
};

export default TemplatesTab;
